<template>
    <div>
        <v-dialog v-on:keydown.27="attemptClose" v-model="display" scrollable :persistent="persistent" :width="loading.data ? 350 : 1350">
            <v-sheet v-if="loading.data" class="pa-4">
                <v-progress-linear indeterminate color="primary" striped></v-progress-linear>
                <div class="text-center">Please wait ...</div>
            </v-sheet>
            <v-card flat v-else>
                <v-toolbar flat>
                    <v-toolbar-title>Journal Voucher</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <p-icon-btn icon="mdi-chart-areaspline-variant" tooltip="Exchange Rates" @click="openExchangeRateDialog"> </p-icon-btn>
                    </v-toolbar-items>
                </v-toolbar>
                <v-card-text class="mt-4">
                    <v-alert type="error" text dense v-for="error in errors" :key="error">{{ error }}</v-alert>
                    <v-form ref="form" v-model="valid">
                        <v-row class="pt-1">
                            <v-col>
                                <v-text-field outlined flat background-color="white" :value="currentTime" readonly dense hide-details="auto" :label="$t('date')" append-icon="mdi-calendar"> </v-text-field>
                            </v-col>
                            <v-col>
                                <p-date-picker v-model="form.document_date" :label="$t('document_date')" outlined hide @update="handleDocumentChangeUpdate" background-color="white"> </p-date-picker>
                            </v-col>
                            <v-col>
                                <v-text-field outlined hide-details="auto" background-color="white" :label="$t('reference')" dense v-model="form.reference"> </v-text-field>
                            </v-col>
                            <v-col>
                                <v-text-field
                                    outlined
                                    hide-details="auto" 
                                    background-color="white" 
                                    :label="$t('code')" 
                                    dense 
                                    disabled
                                    v-model="form.code"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="3">
                                <v-select label="Currency" outlined background-color="white" dense hide-details="auto" :items="allowedCurrencies" item-text="abbreviation" item-value="id" :loading="loading.currencies" v-model="form.currency_id" @change="setJournalCurrency"> </v-select>
                            </v-col>
                        </v-row>
                        <!-- Responsive component is used to limit the hight of the lines. -->
                        <v-responsive max-height="300" class="overflow-y-auto mt-4">
                            <JournalEntryLine
                                v-for="(line, index) in form.lines" :key="line.key" :index="index" v-model="form.lines[index]" :base-currency="baseCurrency" :journal-currency="journalCurrency" :exchange-rates="exchangeRates" :unallowed-selected-accounts-ids="unallowedSelectedAccountsIds" :currently-selected-accounts-objects="currentlySelectedAccountsObjects" @click:delete="handleDeletingItem" @tab:last-item="handleAddingNewLine" @account:object="handleAccountObjectSelection" />
                        </v-responsive>
                        <v-divider class="my-2"></v-divider>
                        <!-- @TODO - shouldn't be journal-entry-line component -->
                        <JournalEntrySummary class="mb-4" :value="totals" :base-currency="baseCurrency" :journal-currency="journalCurrency" />
                        <v-row>
                            <v-col cols="6">
                                <v-textarea outlined background-color="light" v-model="form.narration" hide-details="auto" rows="3" :placeholder="$t('remark')"> </v-textarea>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="primary" :disabled="!journalIsValid" @click="handleSubmitAttempt">{{ $t("save_btn") }}</v-btn>
                    <v-btn text @click="display = false">{{ $t("close_btn") }}</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn text @click="addNewLine">ADD LINE</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <exchange-rate-index v-if="exchangeRateDialogShown" v-model="exchangeRateDialogShown" :exchange-rates.sync="exchangeRates"></exchange-rate-index>
        <AlertDialog ref="alertDialog" />
        <PAlert v-model="alertShown" v-bind="alert"/>
    </div>
</template>
<script>
// Third Parties
import { formatMoney, formatNumber } from "accounting";
import moment from "moment";
import { random, isEmpty, map, round, isNull } from "lodash";
import { mapActions } from "vuex";

// Components
import ExchangeRateIndex from "./ExchangeRateIndex";
import JournalEntryLine from "./JournalEntryLine";
import PIconBtn from "../common/PIconBtn";
import PDatePicker from "./../common/PDatePicker";
import uneditableField from "@/utils/uneditableField";
import JournalEntrySummary from "./JournalEntrySummary.vue";
import AlertDialog from "./../common/AlertDialog.vue";
import PAlert from './../common/PAlert';

// Services
import { searchExchangeRates } from "./../../services/ledger/exchangeRates";
import { getCurrenciesList } from "./../../services/ledger/currency";
import { createJournalVoucher, getJournalVoucherById, updateJournalVoucher } from "./../../services/journal/journalVoucher";
import { getDimensionsIn } from "../../services/ledger/dimension";
import { getActiveAccountsIn } from "../../services/ledger/account";
import { getDpk } from "../../services/journal";

// Utils
import { required, numeric } from "./../../utils/validations";
import { extractCustomRateFromJournalData, populateExchangeRateWithCustomRate, getTotal } from "./../../utils/accounting";

export default {
    name: "JournalEntryDialog",
    components: {
        ExchangeRateIndex,
        JournalEntryLine,
        JournalEntrySummary,
        PIconBtn,
        PDatePicker,
        AlertDialog,
        PAlert
    },
    props: {
        id: {
            type: Number,
            required: false,
        },
        copy: Boolean,
        value: Boolean,
    },
    data() {
        return {
            dpk: null,
            persistent: true,
            currentTime: new moment().format("YYYY-MM-DD"),
            errors: [],
            valid: false,
            baseCurrency: { abbreviation: "NA" },
            journalCurrency: { abbreviation: "NA" },
            loading: {
                data: false,
                currencies: false,
                exchangeRates: false,
                submit: false,
            },
            form: {
                id: null,
                code: null,
                document_date: new moment().format("YYYY-MM-DD"),
                reference: null,
                narration: null,
                currency_id: null,
                lines: [],
            },
            lineSkeleton: {
                key: null,
                account: null,
                account_id: null,
                narration: null,
                base_debit: null,
                base_credit: null,
                debit: null,
                credit: null,
                trans_debit: null,
                trans_credit: null,
                dimension: null,
                dimension_id: null
            },
            rules: { required, numeric },
            currencies: [],
            exchangeRates: [],
            exchangeRateDialogShown: false,
            previouslySelectedAccountObjects: [],
            alertShown: false,
            alert: {
                title: "",
                content: "",
                callback: null
            }
        };
    },
    computed: {
        display: {
            set(val) {
                this.$emit("input", val);
            },
            get() {
                return this.value;
            },
        },
        /**
         * Filters the currencies array to allow only currencies exists
         * in exchange rate to be usable.
         *
         * Base currency will be pushed to the array of allowed currencies by default.
         * It will not be available in the currencies list.
         */
        allowedCurrencies() {
            // Return empty array if exchange rates and currencies.
            if (isEmpty(this.exchangeRates)) return [];
            if (isEmpty(this.currencies)) return [];

            // Currencies IDs that do have exchange rates
            let currencyIdsWithExchangeRate = map(this.exchangeRates, "currency_id");

            // Base currency will be pushed by default.
            currencyIdsWithExchangeRate.push(this.baseCurrency.id);

            return this.currencies.filter((each) => currencyIdsWithExchangeRate.includes(each.id));
        },
        /**
         * Plucks the computed property of allowed currencies to only return ids.
         */
        allowedCurrenciesIds() {
            if (isEmpty(this.allowedCurrencies)) return [];
            return map(this.allowedCurrencies, "id");
        },
        /**
         * Plucks currently selected account ids from the journal lines.
         */
        currentlySelectedAccountsIds() {
            if (isEmpty(this.form.lines)) return [];
            return map(this.form.lines, "account_id");
        },
        /**
         * Uses the currently selected accounts ids to filter the previously
         * selected accounts. Previously selected accounts is pushed whenever
         * an account got selected. Even if it was removed it will still be
         * available.
         *
         * @todo better find a better way other than filtering a previously
         * selected account.
         */
        currentlySelectedAccountsObjects() {
            return this.previouslySelectedAccountObjects.filter((each) => this.currentlySelectedAccountsIds.includes(each.id));
        },
        /**
         * Accounts that is used in the journal entry that doesn't have
         * exchange rate set to it. it should be unallowed account.
         */
        unallowedSelectedAccounts() {
            if (isEmpty(this.currentlySelectedAccountsObjects)) return [];
            return this.currentlySelectedAccountsObjects.filter((each) => !this.allowedCurrenciesIds.includes(each.currency_id));
        },
        /**
         * Plucks unallowedSelectedAccount by id
         */
        unallowedSelectedAccountsIds() {
            return map(this.unallowedSelectedAccounts, "id");
        },
        /**
         * @returns {boolean} If the journal entry contains an account which is not allowed account.
         */
        hasUnallowedAccount() {
            return isEmpty(this.unallowedSelectedAccounts) ? false : true;
        },
        totalBaseDebit() {
            return getTotal(this.form.lines, "base_debit");
        },
        totalBaseCredit() {
            return getTotal(this.form.lines, "base_credit");
        },
        totalTransDebit() {
            return getTotal(this.form.lines, "trans_debit");
        },
        totalTransCredit() {
            return getTotal(this.form.lines, "trans_credit");
        },
        totals() {
            return {
                base_debit: this.totalBaseDebit,
                base_credit: this.totalBaseCredit,
                trans_debit: this.totalTransDebit,
                trans_credit: this.totalTransCredit,
            };
        },
        /**
         * Checks with many conditions to see if the journal is valid or not.
         */
        journalIsValid() {
            if (!this.isBalanced) return false;
            if (this.errors.length > 0) return false;
            if (this.hasUnallowedAccount) return false;
            else return true;
        },
        isBalanced() {
            return this.differenceInBaseCurrency == 0;
            // return this.totalBaseDebit == this.totalBaseCredit && this.totalTransDebit == this.totalTransCredit;
        },
        /**
         * The different between total debit and total credit in base currency.
         */
        differenceInBaseCurrency() {
            return round(this.totalBaseCredit - this.totalBaseDebit, 2);
        },
        // firstNarration() {
        //     if (!this.form.lines.length) return "";
        //     return this.form.lines[0].description;
        // },
        /**
         * Determince
         */
        isEditMode() {
            return !isNull(this.id);
        },
    },
    watch: {
        'form.lines': {
            handler(lines) {
                this.form.narration = lines[0].narration;
            },
            deep: true
        },
        "form.currency_id": {
            handler() {
                this.setJournalCurrency();
            },
        },
    },
    methods: {
        uneditableField,
        formatNumber,
        formatMoney,
        ...mapActions("accounting/account", ["setAccountsLookup"]),
        ...mapActions("accounting/dimension", ["setDimensionsLookup"]),
        handleAddingNewLine(index) {
            // check to see if last row has emitted the event
            if (this.form.lines.length !== index + 1) return;
            this.addNewLine();
        },
        getLastRowDescription() {
            if(!this.form.lines.length) return "";
            return this.form.lines[this.form.lines.length - 1].narration;
        },
        renderDifference(side) {
            if(!["debit", "credit"].includes(side)) throw new Error("Unable to render journal difference.");
            switch(side) {
                case "debit": return this.differenceInBaseCurrency > 0 ? Math.abs(this.differenceInBaseCurrency) : null;
                case "credit": return this.differenceInBaseCurrency < 0 ? Math.abs(this.differenceInBaseCurrency) : null;
            }
        },
        addNewLine() {
            this.form.lines.push({
                ...this.lineSkeleton,
                narration: this.getLastRowDescription(),
                base_debit: this.renderDifference("debit"),
                base_credit: this.renderDifference("credit"),
                key: random(100000, 999999),
            });
        },
        handleDeletingItem(key) {
            // Cannot delete the only line left.
            if (this.form.lines.length === 1) return;
            // filter the line.
            this.form.lines = this.form.lines.filter((each) => each.key != key);
        },
        openExchangeRateDialog() {
            this.exchangeRateDialogShown = true;
        },
        /**
         * This function takes the ID of the base currency and find the
         * base currency object from the currencies array. It then sets
         * the state of baseCurrency as object.
         *
         * This function must be invoked only after all the currencies are avaliable
         *
         * @param {number} baseCurrencyId The ID of the base currency.
         * @returns {void}
         */
        setBaseCurrency(baseCurrencyId) {
            // If the currencies are not well loaded an error should be displayed to the user.
            if (isEmpty(this.currencies)) this.errors.push("Error: Currencies are not available. Please contact System Support!");

            // trying to find the currencies from the currencies array.
            let currency = this.currencies.find((each) => each.id == baseCurrencyId);

            // // Show an error if base currency could not be found.
            if (isEmpty(currency)) {
                this.errors.push("Error: Could not set Base Currency.");
                return;
            }
            // Set the base currency
            this.baseCurrency = currency;
        },
        setJournalCurrency() {
            let currency = this.currencies.find((each) => each.id == this.form.currency_id);
            // Show and error and stop the function if journal currency could not be found.
            if (!currency) {
                this.errors.push("Could not set Journal Currency.");
                return;
            }
            // set the journal currency.
            this.journalCurrency = currency;
        },
        selectBaseCurrencyAsDefaultJournalCurrency() {
            if (!this.baseCurrency.id) {
                this.errors.push("Could not set Base Currency as Default Journal Currency");
                return;
            }
            this.form.currency_id = this.baseCurrency.id;
        },
        handleDocumentChangeUpdate(document_date) {
            searchExchangeRates({ date: document_date }).then((response) => {
                let theExchangeRates = response.data;
                this.exchangeRates = theExchangeRates.rates;

                this.exchangeRates = populateExchangeRateWithCustomRate(this.exchangeRates);
            });
        },
        /**
         * @param {object} account Account object selected from the journal entry line.
         */
        handleAccountObjectSelection(account) {
            if (isEmpty(account)) return;
            // if found return;
            let isFound = this.previouslySelectedAccountObjects.find((each) => each == account);
            if (!isEmpty(isFound)) return;
            this.previouslySelectedAccountObjects = [account, ...this.previouslySelectedAccountObjects];
        },
        /**
         * Prepare the data required from the api and set component initial state.
         *
         * @todo Check active accounts in store before sending the request.
         * @returns {void}
         */
        async init() {
            // 1. Start the loading.
            this.setLoading(true);

            // 2. Initialize variables.
            let baseCurrencyId;
            let theExchangeRates;

            // 3. Send The Request
            try {
                const [currenciesResp, exchangeRatesResp, journalVoucherResp, dpkResp] = await Promise.all([
                    getCurrenciesList(), 
                    searchExchangeRates({ date: this.form.document_date }), 
                    this.isEditMode && getJournalVoucherById(this.id),
                    !this.isEditMode && getDpk()
                ]);

                // 3.1 Set the currencies and exchange rates.
                this.currencies = currenciesResp.data;
                theExchangeRates = exchangeRatesResp.data;
                this.exchangeRates = theExchangeRates.rates;
                this.dpk = dpkResp && dpkResp.data.key;

                // 3.2 check if edit mode get the data from the server.
                if (this.isEditMode) {
                    this.form = journalVoucherResp.data;

                    // 3.2.2 extract accounts ids from form.
                    let accountsIds = map(this.form.lines, "account_id");
                    let dimensionsIds = map(this.form.lines, "dimension_id");

                    // 3.2.3 Get all accounts/dimensions in journal voucher in put in store.
                    const [accountsResp, dimensionsResp] = await Promise.all([getActiveAccountsIn(accountsIds), getDimensionsIn(dimensionsIds)]);
                    var accounts = accountsResp.data;
                    let dimensions = dimensionsResp.data;

                    // dispatch store actions..
                    this.setAccountsLookup(accounts);
                    this.setDimensionsLookup(dimensions);
                    this.previouslySelectedAccountObjects = [...this.previouslySelectedAccountObjects, ...accounts];

                    // check to see if the user intended to copy the journal instead of editting.
                    // If this.copy is true then we should unset the id from the data to avoid editting the old record.
                    if(this.copy == true) {
                        this.form.id = null;
                        this.form.code = null;
                        getDpk().then(resp => this.dpk = resp.data.key)
                    }
                }

                // 3.2.1 Populate Exchange Rate with Custom Rate

                // 3.2.1.1 Extract rates from data.
                let customRates = null;
                if (this.isEditMode) customRates = extractCustomRateFromJournalData(this.form, accounts);

                // 3.2.1.3 Map the array with the cusom rate
                this.exchangeRates = populateExchangeRateWithCustomRate(this.exchangeRates, customRates);

                // 3.3 Find the base currency and filter set it as base currency.
                baseCurrencyId = parseInt(theExchangeRates.base_currency_id);
                this.setBaseCurrency(baseCurrencyId);

                // 3.4 Set the initail journal currency.
                if (!this.isEditMode) this.selectBaseCurrencyAsDefaultJournalCurrency();

                // 3.5 Stop the loading
                this.setLoading(false);
            } catch (error) {
                let message = error.response ? error.response.data.message : error;
                this.$refs.alertDialog.open({ type: "error", message });
                this.persistent = false;
                console.log(error);
            }
        },
        setLoading(value) {
            this.loading.currencies = value;
            this.loading.exchangeRates = value;
            this.loading.data = value;
        },
        handleSubmitAttempt() {
            this.alertShown = true;
            this.alert.title = "Alert";
            if(this.copy) this.alert.content = "Do you really want to save copied voucher?";
            else if(this.id) this.alert.content = "Do you want to save changes?";
            else this.alert.content = "Do you want to create new journal voucher?"
            this.alert.callback = this.submit;
        },
        async submit() {
            if (!this.$refs.form.validate()) return;
            this.loading.submit = true;
            try {
                // decide update or create new record.
                var response;
                if (this.form.id == null) response = await createJournalVoucher(this.form, this.dpk);
                else response = await updateJournalVoucher(this.form);

                // emit event
                this.$emit("submit", response.data.record);
                this.$refs.alertDialog.open({
                    type: "success",
                    message: response.data.message,
                });

                // other.
                this.loading.submit = false;
                this.display = false;
            } catch (error) {
                console.log(error);
                this.loading.submit = false;
                let message = error.response ? error.response.data.message : error;
                this.$refs.alertDialog.open({ type: "error", message });
            }
        },
    },
    created() {
        // 1. Add The first line in the journal voucher.
        this.addNewLine();
        // 2. Fetch data and prepare the inital state.
        this.init();
    },
    updated() {},
};
</script>
